// RevealNav — the ordrs marketing nav.
//
// Model: a fixed top bar with an enlarged ordrs logo (routes HOME) and a small
// separate trigger that opens a LEFT-THIRD panel menu. The panel is fixed; the
// page stays visible to the right and clicking it closes the menu. Each menu
// item paints its own panel background on hover (DS per-item colors now;
// storefront images later). Sterling-Gate "quiet luxury": big left-aligned
// Archivo Black rows. Built on DS tokens + --ease-out (no GSAP).
//
// Items: Demo, Pricing, Solutions, About, Gallery, Blog, Contact.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_instant-menu-preview/artifacts/9yxh3mar_neural.logo%20%281%29.png";

// Animated counter and accent for menu row
function AnimatedIndex({ num, isHovered }) {
  return (
    <motion.span
      className="revealnav-row__index ds-mono"
      initial={{ opacity: 0.5, scale: 1 }}
      animate={{
        opacity: isHovered ? 1 : 0.5,
        scale: isHovered ? 1.1 : 1
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {String(num).padStart(2, "0")}
    </motion.span>
  );
}

// Kinetic accent bar that animates in on hover
function HoverAccent({ isHovered }) {
  return (
    <motion.div
      className="revealnav-row__accent"
      initial={{ width: 0, opacity: 0 }}
      animate={{
        width: isHovered ? "100%" : 0,
        opacity: isHovered ? 1 : 0
      }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    />
  );
}

// Each item carries the panel background it paints on hover (DS palette).
const MENU = [
  {
    label: "Demo",
    to: "/demo",
    bg: "var(--sienna-200, #f0d5c8)",
    fg: "var(--ink-900)",
  },
  {
    label: "Pricing",
    to: "/pricing",
    bg: "var(--sienna-100, #ebd1c5)",
    fg: "var(--ink-900)",
  },
  {
    label: "Solutions",
    to: "/solutions",
    bg: "var(--sienna-300, #e8b89a)",
    fg: "var(--ink-900)",
  },
  {
    label: "About",
    to: "/about",
    bg: "var(--sienna-50, #f5e6df)",
    fg: "var(--ink-900)",
  },
  {
    label: "Gallery",
    to: "/gallery",
    bg: "var(--sienna-200, #ffb688)",
    fg: "var(--ink-900)",
  },
  {
    label: "Blog",
    to: "https://blog.ordrs.ai",
    bg: "var(--sienna-200, #fdb38f)",
    fg: "var(--ink-900)",
    external: true,
  },
  {
    label: "Contact",
    to: "/contact",
    bg: "var(--sienna-100, #e48346)",
    fg: "var(--ink-900)",
  },
];

export default function RevealNav({ ghost = false }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [barVisible, setBarVisible] = useState(false);

  useEffect(() => {
    const persisted = window.sessionStorage.getItem("revealnav-open");
    if (persisted === "true") setOpen(true);
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem("revealnav-open", open ? "true" : "false");
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = (to, external) => {
    if (external) {
      window.open(to, "_blank");
      setOpen(false);
      return;
    }
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (window.location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        nav("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } else {
      nav(to);
    }
    setOpen(false);
  };

  const panel = hovered != null ? MENU[hovered] : null;
  const panelBg = panel ? panel.bg : "var(--cream-100)";
  const panelFg = panel ? panel.fg : "var(--ink-900)";

  // In ghost mode: invisible corner zone that reveals the bar on hover.
  // We watch the zone (not the bar itself) so the hitbox stays large even when bar is faded.
  const ghostZoneStyle = ghost ? {
    position: "fixed", top: 0, left: 0, width: 160, height: 140, zIndex: 61,
    cursor: "default",
  } : null;

  const barStyle = ghost ? {
    opacity: barVisible || open ? 1 : 0,
    pointerEvents: barVisible || open ? "auto" : "none",
    transition: "opacity 0.25s ease",
  } : {};

  return (
    <>
      {/* Ghost hover zone — covers logo + trigger area, shows bar on enter */}
      {ghost && (
        <div
          style={ghostZoneStyle}
          onMouseEnter={() => setBarVisible(true)}
          onMouseLeave={() => setBarVisible(false)}
          aria-hidden
        />
      )}

      {/* fixed top bar: enlarged logo (-> home) + separate menu trigger */}
      <header className="revealnav-bar" style={barStyle}>
        <Link to="/" className="revealnav-logo" aria-label="ordrs home" data-testid="nav-home">
          <img src={LOGO_URL} alt="ordrs" />
        </Link>
        <button
          className="revealnav-trigger"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-label="Open menu"
          data-testid="nav-menu-trigger"
        >
          <i className="revealnav-trigger__lines" aria-hidden><i /><i /><i /></i>
        </button>
      </header>

      {/* click-off scrim over the visible 2/3 page */}
      <div
        className={`revealnav-scrim${open ? " is-open" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden
      />

      {/* left-third fixed panel */}
      <aside
        className={`revealnav-panel${open ? " is-open" : ""}`}
        style={{ "--panel-bg": panelBg, "--panel-fg": panelFg }}
        role="dialog"
        aria-modal="true"
      >
        <button className="revealnav-close" onClick={() => setOpen(false)} aria-label="Close menu">
          <span /><span />
        </button>

        <nav className="revealnav-menu">
          {MENU.map((item, i) => {
            const isHovered = hovered === i;
            return (
              <button
                key={item.label}
                className="revealnav-row"
                style={{ "--row-delay": `${0.05 * i + 0.06}s` }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => go(item.to, item.external)}
              >
                <AnimatedIndex num={i + 1} isHovered={isHovered} />
                <motion.span
                  className="revealnav-row__label"
                  initial={{ scale: 1, letterSpacing: "0em" }}
                  animate={{
                    scale: isHovered ? 1.05 : 1,
                    letterSpacing: isHovered ? "0.05em" : "0em"
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {item.label}
                </motion.span>
                <HoverAccent isHovered={isHovered} />
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
